import type { ComponentProps } from "react";
import { Slot } from "radix-ui";
import { ChevronRight, MoreHorizontal } from "lucide-react";
import { cn } from "../lib/cn";
import { microcopy } from "../lib/microcopy";

/**
 * The UAE Design System 3.0 breadcrumb (`aegov-breadcrumb`) on shadcn/ui's parts: gold links, underlined, the current page in semibold
 * AE Black 800, chevron separators that flip for Arabic. The links take AE Gold 700 rather than the Design System's 600, which falls
 * under 4.5:1 on the AE Black 50 canvas the apps' header sits on.
 */
export function Breadcrumb({ "aria-label": ariaLabel, ...props }: ComponentProps<"nav">) {
  return <nav aria-label={ariaLabel ?? microcopy("breadcrumb")} data-slot="breadcrumb" {...props} />;
}

export function BreadcrumbList({ className, ...props }: ComponentProps<"ol">) {
  return <ol data-slot="breadcrumb-list" className={cn("flex min-w-0 flex-wrap items-center gap-1 text-sm break-words text-aegold-700 sm:gap-1.5", className)} {...props} />;
}

export function BreadcrumbItem({ className, ...props }: ComponentProps<"li">) {
  return <li data-slot="breadcrumb-item" className={cn("inline-flex min-w-0 items-center gap-1.5", className)} {...props} />;
}

export function BreadcrumbLink({ asChild, className, ...props }: ComponentProps<"a"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "a";
  return <Comp data-slot="breadcrumb-link" className={cn("truncate rounded-sm font-medium text-aegold-700 underline decoration-1 underline-offset-2 transition-colors hover:text-aegold-800 hover:decoration-2", className)} {...props} />;
}

export function BreadcrumbPage({ className, ...props }: ComponentProps<"span">) {
  return <span data-slot="breadcrumb-page" aria-current="page" className={cn("truncate font-semibold text-aeblack-800", className)} {...props} />;
}

export function BreadcrumbSeparator({ children, className, ...props }: ComponentProps<"li">) {
  return (
    <li data-slot="breadcrumb-separator" role="presentation" aria-hidden="true" className={cn("text-aegold-700 [&>svg]:size-3.5 rtl:[&>svg]:-scale-x-100", className)} {...props}>
      {children ?? <ChevronRight />}
    </li>
  );
}

export function BreadcrumbEllipsis({ className, ...props }: ComponentProps<"span">) {
  return (
    <span data-slot="breadcrumb-ellipsis" role="presentation" aria-hidden="true" className={cn("flex size-7 items-center justify-center", className)} {...props}>
      <MoreHorizontal className="size-4" />
      <span className="sr-only">{microcopy("more")}</span>
    </span>
  );
}
